"use client"

import { CircleCheck, Info, OctagonX, TriangleAlert, X, type LucideIcon } from "lucide-react"
import { Toaster as SonnerToaster, toast as sonnerToast } from "sonner"

import { Button } from "@/components/ui/button"

/**
 * EVERY toast in the app: what one looks like, how long it lives, and where they stack.
 *
 * There were two systems and they disagreed about all three.
 *
 *   · This file's `toast({ text, icon })`, used by bookmarks, feedback and copy-to-clipboard —
 *     a custom card with an icon in a disc.
 *   · Raw `sonner` — `toast.error("…", { description })` and
 *     `toast("Title", { description, duration: Infinity, action: { label: "Dismiss" } })` —
 *     used by sign-in, sign-up, preferences, the research canvas, the job poller, projects and
 *     the read page. Sonner's own default rendering, nothing like the first.
 *
 * So the same product told you a bookmark was saved in one visual language and that your session
 * had expired in another, and whether a message stayed on screen depended on which file it was
 * raised from. Worse, three pages each mounted their OWN `<Toaster>` on top of the global one:
 * sonner renders every toast once per mounted Toaster, so a sign-in error appeared twice.
 *
 * There is one Toaster now — `AppToaster`, mounted once in providers — and one way to raise a
 * message. Both are defined here, because the container's position and the card's shape are the
 * same design decision and were previously made in different files.
 *
 * The API deliberately mirrors sonner's, so `toast.error("…", { description })` reads the same
 * as before: for most call sites the migration was changing the import and nothing else.
 */

export type ToastTone = "success" | "error" | "warning" | "info"

/** The icon each tone speaks with, so no call site has to choose one. */
const TONE_ICON: Record<ToastTone, LucideIcon> = {
  success: CircleCheck,
  error: OctagonX,
  warning: TriangleAlert,
  info: Info,
}

/**
 * How long each tone stays.
 *
 * Failures last longer than confirmations: a confirmation tells you what you already know you
 * asked for, while a failure is news, and often has a reason attached that takes a moment to
 * read. `dismiss: true` overrides both and waits for the reader.
 */
const TONE_DURATION: Record<ToastTone, number> = {
  success: 3000,
  info: 3000,
  warning: 5000,
  error: 5000,
}

export interface ToastOptions {
  /** The message. One line, sentence case. */
  text: string
  /** The detail under it — a server's reason for refusing, usually. */
  description?: string
  tone?: ToastTone
  /** Overrides the tone's icon. Rarely needed; `Check` for copy-to-clipboard is the exception. */
  icon?: LucideIcon
  /** Stay until dismissed, with an X to do it. For anything the reader must actually read. */
  dismiss?: boolean
  /** Overrides the tone's duration. */
  duration?: number
}

function show(options: ToastOptions) {
  const tone = options.tone ?? "info"
  return sonnerToast.custom(
    (id) => (
      <ToastCard
        id={id}
        text={options.text}
        description={options.description}
        icon={options.icon ?? TONE_ICON[tone]}
        dismiss={options.dismiss}
      />
    ),
    { duration: options.dismiss ? Infinity : (options.duration ?? TONE_DURATION[tone]) },
  )
}

/**
 * Raise a toast.
 *
 * Takes either an options object — `toast({ text, icon })`, the form this file always had — or
 * a message and options, which is sonner's form and what the migrated call sites use.
 */
function toastFn(input: ToastOptions | string, options?: Omit<ToastOptions, "text">) {
  return show(typeof input === "string" ? { ...options, text: input } : input)
}

/** `toast.error("…", { description })`, matching sonner's shape so call sites read unchanged. */
const withTone =
  (tone: ToastTone) =>
  (text: string, options?: Omit<ToastOptions, "text" | "tone">) =>
    show({ ...options, text, tone })

export const toast = Object.assign(toastFn, {
  success: withTone("success"),
  error: withTone("error"),
  warning: withTone("warning"),
  info: withTone("info"),
  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
})

function ToastCard({
  id,
  text,
  description,
  icon: Icon,
  dismiss,
}: {
  id: string | number
  text: string
  description?: string
  icon?: LucideIcon
  dismiss?: boolean
}) {
  return (
    /*
      `rounded-2xl` and a border, like every other card in the app. It was `rounded-md` with a
      shadow and no border, which is the one surface here that looked like stock shadcn.

      A plain string rather than `cn`: lib/utils imports `toast` from this file for
      copy-to-clipboard, so importing `cn` back out of it made a genuine circular import. There
      is nothing conditional here to merge anyway.
    */
    <div className="flex w-full flex-row items-start gap-4 rounded-2xl border border-border bg-background p-4 shadow-[0_0_24px_rgba(0,0,0,0.08)] md:w-[364px]">
      {Icon && (
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-secondary">
          <Icon className="size-4 shrink-0 text-foreground" strokeWidth={2} />
        </span>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-1 py-1.5">
        <span className="text-sm font-medium leading-snug text-foreground">{text}</span>
        {description && (
          <span className="text-[13px] font-normal leading-relaxed text-muted-foreground text-pretty">
            {description}
          </span>
        )}
      </div>

      {dismiss && (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Dismiss"
          onClick={() => sonnerToast.dismiss(id)}
          className="shrink-0 text-muted-foreground hover:text-foreground"
        >
          <X />
        </Button>
      )}
    </div>
  )
}

/**
 * The one Toaster. Mounted once, in providers.
 *
 * Never mount a second one anywhere: sonner renders each toast once per Toaster on the page, so
 * a page with its own becomes a page that shows everything twice. Three pages had one.
 */
export function AppToaster() {
  return <SonnerToaster position="bottom-right" gap={12} offset={24} />
}
